"use client";

/**
 * Client for the site level telemetry endpoints (A3 self-consumption split,
 * A6 battery state and health, A7 portfolio league table).
 *
 * Every call goes through fetchWithAuth, so the organisation is the one on the
 * verified token; nothing here accepts an organisation from the page. A non-OK
 * response is surfaced as an error with the server's detail text rather than
 * being swallowed into an empty chart, because "the service refused" and "the
 * site has no data" are different facts and the screen must show which.
 */

import { useEffect, useState } from "react";
import { fetchWithAuth } from "@/lib/api";

export type Quality = "measured" | "unmarked" | "interpolated" | "estimated" | "invalid" | "missing";
export type Provenance = "device" | "unknown" | "synthetic";

/** Where a consumer's half hours came from: the stored energy ledger, an
 *  on-request computation from the readings, or both. */
export type LedgerSource = "ledger" | "computed" | "mixed";

export type LedgerProvenance = {
  source: LedgerSource;
  periods_from_ledger: number;
  periods_computed: number;
  periods_stale: number;
  ledger_reason: string | null;
  calculation_version: string;
};

export type SiteRecord = {
  site_id: string;
  name: string;
  assets: { solar: string[]; meter: string[]; battery: string[] };
  pv_kwp: number | null;
  pv_kwp_source: "site_profile" | "device_record" | "unavailable";
  battery_nameplate_kwh: number | null;
  battery_nameplate_source: "site_profile" | "device_record" | "unavailable";
  profile: { floor_area_m2: number | null; opening_hours_per_week: number | null; source: string | null } | null;
  profile_readable: boolean;
};

export type SitesResponse = {
  organisation_id: string;
  sites: SiteRecord[];
  source: "device_records" | "timeseries_unavailable";
};

export type SplitInput = {
  quality: Quality;
  provenance: Provenance;
  samples: number;
  invalid_samples?: number;
  slots_present: number;
  slots_invalid?: number;
  slots_missing: number;
};

export type SplitBucket = {
  key: string;
  period_start: string;
  period_end: string;
  settlement_day?: string;
  settlement_period?: number;
  generation_kwh: number | null;
  self_consumed_kwh: number | null;
  exported_kwh: number | null;
  self_consumed_pct: number | null;
  exported_pct: number | null;
  grid_import_kwh: number | null;
  grid_export_kwh: number | null;
  export_beyond_generation_kwh: number | null;
  battery_charge_kwh: number | null;
  battery_discharge_kwh: number | null;
  quality: Quality;
  provenance: Provenance;
  complete: boolean;
  coverage_pct: number;
  slots_expected: number;
  slots_complete: number;
  inputs: { solar: SplitInput; meter: SplitInput; battery?: SplitInput };
};

export type SplitResponse = {
  organisation_id: string;
  site_id: string;
  site: SiteRecord | null;
  resolution: "hh" | "daily" | "monthly";
  from: string;
  to: string;
  calendar: string;
  settlement_aligned: boolean;
  method: string;
  buckets: SplitBucket[];
  total: Omit<SplitBucket, "key" | "period_start" | "period_end"> | null;
  source: "timeseries" | "device_records" | "timeseries_unavailable" | LedgerSource;
  unavailable_reason: string | null;
  ledger?: LedgerProvenance | null;
};

export class ApiError extends Error {
  status: number;
  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
  }
}

export async function getJson<T>(url: string): Promise<T> {
  const res = (await fetchWithAuth(url)) as Response;
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body && typeof body.detail === "string") detail = body.detail;
    } catch {
      /* body was not JSON; keep the status line */
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export type Loadable<T> = { state: "loading" } | { state: "ready"; data: T } | { state: "error"; message: string };

/** The organisation's sites, from its own device records. */
export function useSites(): Loadable<SitesResponse> {
  const [value, setValue] = useState<Loadable<SitesResponse>>({ state: "loading" });
  useEffect(() => {
    let cancelled = false;
    getJson<SitesResponse>("/api/telemetry/sites")
      .then((data) => {
        if (!cancelled) setValue({ state: "ready", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) setValue({ state: "error", message: err instanceof Error ? err.message : "Request failed" });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return value;
}

export const fmtKwh = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined || !Number.isFinite(v) ? "not available" : `${v.toLocaleString(undefined, { maximumFractionDigits: digits })} kWh`;

export const fmtPct = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined || !Number.isFinite(v) ? "n/a" : `${v.toFixed(digits)}%`;

export const fmtNum = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined || !Number.isFinite(v) ? "n/a" : v.toLocaleString(undefined, { maximumFractionDigits: digits });

export function qualityTone(q: Quality | string | null | undefined): string {
  switch (q) {
    case "measured":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "estimated":
      return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "interpolated":
      return "bg-sky-500/15 text-sky-300 border-sky-500/30";
    case "invalid":
      return "bg-red-500/10 text-red-200 border-red-500/60";
    case "missing":
      return "bg-rose-500/15 text-rose-300 border-rose-500/30";
    default:
      return "bg-slate-500/15 text-slate-300 border-slate-500/30";
  }
}

export function qualityExplanation(q: Quality | string | null | undefined): string {
  switch (q) {
    case "measured":
      return "Every input sample in this bucket was marked measured by its source.";
    case "estimated":
      return "At least one input sample in this bucket was estimated or interpolated by its source.";
    case "interpolated":
      return "At least one input sample in this bucket was interpolated from neighbouring readings.";
    case "invalid":
      return "At least one input in this bucket was heard from and every sample it sent failed validation. Its energy is unknown, not zero.";
    case "missing":
      return "No half hour in this bucket had both an inverter and a meter reading.";
    default:
      return "The source asserted nothing about measurement quality. Not promoted to measured.";
  }
}
